import React, { useState, useEffect, useRef } from 'react'
import { supabase } from './supabase'
import { trackLead } from './metaPixel'

// Generic bilingual event funnel (/funnel/team-events, /funnel/all-events).
// Rendered by FunnelLanding when get_funnel_public returns variant = 'event'.
// No agent name on the page: the fliers carry the people. Leads go through
// submit_event_lead (supabase/schema/2026-10-23_event_funnels.sql), which takes
// the FUB source from the funnel row, then send-funnel-lead-to-fub.
//
// Logo rule: a brokerage logo image shows only for an exact match in
// BROKERAGE_LOGOS (HomePros, HomePros Advantage). Any other brokerage shows its
// name as text, so no office can ever show another office's logo.

// Tiles, in display order (2026-10-29: Refinance removed). 'unsure' is not a
// tile: it is what Submit sends when nothing is picked (submit_event_lead accepts both 'insurance' and 'unsure').
const INTENTS = ['buy', 'sell', 'behind', 'insurance']

const T = {
  en: {
    live: 'Live at the event',
    h1a: 'Your next move', h1b: 'starts tonight.',
    subA: 'Buying, selling, behind on payments, or looking for life insurance? Tell us in ',
    subB: '30 seconds',
    subC: '. A real person on our team follows up, in English or Spanish.',
    p1: 'Free and confidential', p2: 'Hablamos español', p3: 'Fast follow-up',
    step: 'Step', of: 'of', time: '30 sec',
    q1: 'Are you interested in any of these services?', q1s: 'Pick any that apply, or just submit.',
    tiles: {
      buy: ['Buy a home', 'Find it, negotiate it, win it'],
      sell: ['Sell a home', 'Know what it’s worth today'],
      behind: ['Behind on payments', 'You have more options than you think'],
      insurance: ['Life insurance', 'Protect your family'],
    },
    unsure: 'Not sure yet',
    note: 'Other / notes (optional)',
    noteIns: 'Life insurance is offered by Danae Marquez, licensed life insurance producer (AZ #22213864). Insurance products are offered separately from and are not affiliated with HomePros or HomePros Advantage real estate services.',
    noteBehind: 'Free. Confidential. No judgment. A short sale or loan workout may stop foreclosure.',
    addr: 'Property address', next: 'Continue',
    q2: 'Where should we reach you?', q2s: 'No spam. One person, one call.',
    name: 'Full name', phone: 'Mobile phone', email: 'Email',
    send: 'Submit', sending: 'Sending…', back: '← Back',
    fine: 'By submitting, you agree to be contacted by phone, text and email. Free and confidential. No obligation. This is not a commitment to lend.',
    e2: 'Enter the property address.',
    e3: 'Enter your name, a valid mobile number, and a valid email.',
    eSend: 'Something went wrong. Please try again.',
    doneH: 'You’re in.',
    doneP: 'Our team has your request. Expect a text or call shortly, so keep your phone close.',
    tick: ['Buy', 'Sell', 'Short sale', 'Life insurance', 'First-time buyers', 'Hablamos español', 'Comprar', 'Vender', 'Venta corta', 'Seguro de vida'],
    howH: 'Here’s what happens next',
    s1h: 'We reach out', s1p: 'A real person from our team contacts you, usually the same day.',
    s2h: 'We map your options', s2p: 'Buy, sell, stop a foreclosure or protect your family: you get a clear plan with real numbers.',
    s3h: 'You decide', s3p: 'No pressure, no obligation. You move when you’re ready.',
    legalA: ' is a licensed Arizona real estate brokerage',
    legalB: '. We are not a lender, attorney, or government agency. Short sales require lender approval; results not guaranteed. Not legal or tax advice. Not a solicitation if you are already working with an agent. Life insurance is offered separately by a licensed insurance producer and is not affiliated with HomePros real estate services. Equal Housing Opportunity.',
  },
  es: {
    live: 'En vivo en el evento',
    h1a: 'Su próximo paso', h1b: 'empieza hoy.',
    subA: '¿Comprar, vender, atrasado en sus pagos o busca seguro de vida? Díganos en ',
    subB: '30 segundos',
    subC: '. Una persona real de nuestro equipo le contacta, en español o inglés.',
    p1: 'Gratis y confidencial', p2: 'We speak English', p3: 'Respuesta rápida',
    step: 'Paso', of: 'de', time: '30 seg',
    q1: '¿Le interesa alguno de estos servicios?', q1s: 'Elija los que apliquen, o solo envíe.',
    tiles: {
      buy: ['Comprar casa', 'Encontrarla, negociarla, ganarla'],
      sell: ['Vender casa', 'Sepa cuánto vale hoy'],
      behind: ['Atrasado en sus pagos', 'Tiene más opciones de las que cree'],
      insurance: ['Seguro de vida', 'Proteja a su familia'],
    },
    unsure: 'Todavía no sé',
    note: 'Otro / notas (opcional)',
    noteIns: 'El seguro de vida lo ofrece Danae Marquez, productora de seguros de vida con licencia (AZ #22213864). Los productos de seguros se ofrecen de forma separada y no están afiliados con los servicios de bienes raíces de HomePros ni HomePros Advantage.',
    noteBehind: 'Gratis. Confidencial. Sin juicios. Una venta corta o un arreglo con el banco puede detener la ejecución hipotecaria.',
    addr: 'Dirección de la propiedad', next: 'Continuar',
    q2: '¿Dónde le contactamos?', q2s: 'Sin spam. Una persona, una llamada.',
    name: 'Nombre completo', phone: 'Celular', email: 'Correo electrónico',
    send: 'Enviar', sending: 'Enviando…', back: '← Regresar',
    fine: 'Al enviar, acepta ser contactado por teléfono, texto y correo. Gratis y confidencial. Sin compromiso. Esto no es un compromiso de préstamo.',
    e2: 'Escriba la dirección de la propiedad.',
    e3: 'Escriba su nombre, un celular válido y un correo válido.',
    eSend: 'Algo salió mal. Por favor intente de nuevo.',
    doneH: '¡Listo!',
    doneP: 'Nuestro equipo recibió su solicitud. Espere un texto o llamada muy pronto; tenga su teléfono a la mano.',
    tick: ['Comprar', 'Vender', 'Venta corta', 'Seguro de vida', 'Primera casa', 'We speak English', 'Buy', 'Sell', 'Short sale', 'Life insurance'],
    howH: 'Lo que sigue',
    s1h: 'Le contactamos', s1p: 'Una persona real de nuestro equipo le llama, normalmente el mismo día.',
    s2h: 'Revisamos sus opciones', s2p: 'Comprar, vender, detener una ejecución o proteger a su familia: le damos un plan claro con números reales.',
    s3h: 'Usted decide', s3p: 'Sin presión, sin compromiso. Usted avanza cuando esté listo.',
    legalA: ' es una correduría de bienes raíces con licencia en Arizona',
    legalB: '. No somos prestamistas, abogados ni agencia del gobierno. Las ventas cortas requieren aprobación del banco; no se garantizan resultados. No es asesoría legal ni fiscal. No es una solicitud si ya trabaja con un agente. El seguro de vida lo ofrece por separado una productora de seguros con licencia y no está afiliado con los servicios de bienes raíces de HomePros. Igualdad de oportunidades de vivienda.',
  },
}

// Office line in the footer legal text, only for the brokerage it belongs to.
const HOMEPROS_OFFICE = '9059 W McDowell Rd, Phoenix, AZ'

function resolveInitialLang(rowLangDefault) {
  const params = new URLSearchParams(window.location.search)
  const q = (params.get('lang') || '').toLowerCase()
  if (q === 'en' || q === 'es') return q
  return String(rowLangDefault || '').toLowerCase() === 'es' ? 'es' : 'en'
}

// Optional ?e=<event name> from the flier QR code. Cleaned the same way the
// database cleans it (submit_event_lead), so what we send is what gets stored.
function readEventRef() {
  try {
    const raw = new URLSearchParams(window.location.search).get('e') || ''
    const clean = raw.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 40)
    return clean || null
  } catch (_) {
    return null
  }
}

function isValidEmail(s) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || '').trim())
}
function isValidPhone(s) {
  return String(s || '').replace(/\D/g, '').length >= 10
}

function splitName(full) {
  const parts = String(full || '').trim().split(/\s+/).filter(Boolean)
  return { first: parts[0] || '', last: parts.slice(1).join(' ') }
}

// Brokerage logos (public/). A brokerage not listed here shows its name as
// text from the profile, so no office can ever show another office's logo.
const BROKERAGE_LOGOS = {
  'HomePros': '/homepros-logo.png',
  'HomePros Advantage': '/homepros-advantage-logo.png',
}

function BrokerageMark({ brokerage, footer }) {
  const name = String(brokerage || '')
  if (BROKERAGE_LOGOS[name]) {
    return <img className={footer ? 'efl-logo efl-logo-foot' : 'efl-logo'} src={BROKERAGE_LOGOS[name]} alt={name} />
  }
  return <span className={footer ? 'efl-brand-text efl-brand-foot' : 'efl-brand-text'}>{name}</span>
}

// ── Icons (inline, currentColor) ─────────────────────────────────────────
const svgProps = { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true }
const Icon = {
  buy: () => <svg {...svgProps}><path d="M3 11l9-7 9 7v9a1 1 0 01-1 1h-5v-6H9v6H4a1 1 0 01-1-1z" /></svg>,
  sell: () => <svg {...svgProps}><path d="M20.6 13.4l-7.2 7.2a2 2 0 01-2.8 0L3 13V3h10l7.6 7.6a2 2 0 010 2.8z" /><circle cx="7.5" cy="7.5" r="1.5" /></svg>,
  behind: () => <svg {...svgProps}><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="4" /><path d="M5.6 5.6l3.6 3.6M14.8 14.8l3.6 3.6M18.4 5.6l-3.6 3.6M9.2 14.8l-3.6 3.6" /></svg>,
  insurance: () => <svg {...svgProps}><path d="M12 3l8 4v5c0 5-3.5 8-8 9-4.5-1-8-4-8-9V7z" /><path d="M12 16.5s-4-2.4-4-5.1A2.1 2.1 0 0112 10a2.1 2.1 0 014 1.4c0 2.7-4 5.1-4 5.1z" /></svg>,
  Check: () => <svg {...svgProps} strokeWidth={3.5}><path d="M5 12l5 5L20 7" /></svg>,
  Arrow: () => <svg {...svgProps} strokeWidth={2.5}><path d="M5 12h14M13 6l6 6-6 6" /></svg>,
  pin: () => <svg {...svgProps}><path d="M12 22s7-6.2 7-12a7 7 0 10-14 0c0 5.8 7 12 7 12z" /><circle cx="12" cy="10" r="2.5" /></svg>,
  user: () => <svg {...svgProps}><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0116 0" /></svg>,
  phone: () => <svg {...svgProps}><rect x="6" y="2" width="12" height="20" rx="3" /><path d="M11 18h2" /></svg>,
  mail: () => <svg {...svgProps}><rect x="3" y="5" width="18" height="14" rx="3" /><path d="M3 7l9 6 9-6" /></svg>,
  Shield: () => <svg {...svgProps}><path d="M12 3l8 4v5c0 5-3.5 8-8 9-4.5-1-8-4-8-9V7z" /><path d="M9 12l2 2 4-4" /></svg>,
  Lang: () => <svg {...svgProps}><path d="M5 8l6 6M4 14l6-6 2-3M2 5h12M7 2h1M22 22l-5-10-5 10M14 18h6" /></svg>,
  Bolt: () => <svg {...svgProps}><path d="M13 2L3 14h9l-1 8 10-12h-9z" /></svg>,
}

// Drawn flags (emoji flags render as "US"/"MX" letters on Windows).
const FlagUS = () => (
  <svg className="efl-flag" viewBox="0 0 38 20" aria-hidden="true">
    <rect width="38" height="20" fill="#b22234" />
    <g fill="#fff">{[1.54, 4.62, 7.69, 10.77, 13.85, 16.92].map(y => <rect key={y} y={y} width="38" height="1.54" />)}</g>
    <rect width="15.2" height="10.77" fill="#3c3b6e" />
    <g fill="#fff">
      {[[2.5, 2], [6.3, 2], [10.1, 2], [13.2, 2], [4.4, 4.2], [8.2, 4.2], [12, 4.2], [2.5, 6.4], [6.3, 6.4], [10.1, 6.4], [13.2, 6.4], [4.4, 8.6], [8.2, 8.6], [12, 8.6]]
        .map(([cx, cy]) => <circle key={cx + '-' + cy} cx={cx} cy={cy} r=".7" />)}
    </g>
  </svg>
)
const FlagMX = () => (
  <svg className="efl-flag" viewBox="0 0 30 20" aria-hidden="true">
    <rect width="10" height="20" fill="#006847" />
    <rect x="10" width="10" height="20" fill="#fff" />
    <rect x="20" width="10" height="20" fill="#ce1126" />
    <ellipse cx="15" cy="10.6" rx="2.9" ry="2.5" fill="none" stroke="#3e8e41" strokeWidth=".9" />
    <ellipse cx="15" cy="9.6" rx="1.5" ry="1.7" fill="#8c5a2b" />
    <rect x="13.6" y="12.6" width="2.8" height=".8" rx=".4" fill="#1a73b5" />
  </svg>
)

function Field({ id, icon, label, type, value, onChange, bad, autoComplete, inputMode }) {
  const Ic = Icon[icon]
  return (
    <div className={'efl-fld' + (bad ? ' bad' : '')}>
      <Ic />
      <input id={id} type={type || 'text'} placeholder=" " value={value} onChange={e => onChange(e.target.value)}
        autoComplete={autoComplete} inputMode={inputMode} />
      <label htmlFor={id}>{label}</label>
    </div>
  )
}

export default function EventFunnelLanding({ row }) {
  const [lang, setLang] = useState(() => resolveInitialLang(row.lang_default))
  const [step, setStep] = useState(1)           // 1 intents, 2 contact, 3 done
  const [sel, setSel] = useState([])            // subset of INTENTS, in INTENTS order
  const [form, setForm] = useState({ addr: '', name: '', phone: '', email: '', note: '' })
  const [err, setErr] = useState('')
  const [tried, setTried] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const cardRef = useRef(null)
  const firstStepRender = useRef(true)

  const t = T[lang] || T.en
  const selling = sel.includes('sell')

  useEffect(() => { document.documentElement.lang = lang }, [lang])

  // The top bar (logo + language) stays pinned; it gets a frosted background
  // once the page scrolls so it stays readable over the content below.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Bring the card into view when the step changes (not on first load).
  useEffect(() => {
    if (firstStepRender.current) { firstStepRender.current = false; return }
    // Scroll the WINDOW, not scrollIntoView: scrollIntoView also scrolls the
    // overflow-hidden hero box itself, which uncovers a strip of the photo.
    const el = cardRef.current
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 84, behavior: 'smooth' })
  }, [step])

  function toggle(k) {
    setErr('')
    setSel(s => (s.includes(k) ? s.filter(x => x !== k) : INTENTS.filter(x => x === k || s.includes(x))))
  }
  function set(k, v) { setForm(f => ({ ...f, [k]: v })) }

  const addrOk = !selling || form.addr.trim().length >= 5
  const nameOk = form.name.trim().length >= 2
  const phoneOk = isValidPhone(form.phone)
  const emailOk = isValidEmail(form.email)

  const chipLabel = k => (k === 'unsure' ? t.unsure : t.tiles[k][0])

  // 2026-10-28 (Josh): contact info first (step 1), then what they need
  // (step 2), which submits. Skip submits right away as intents ['unsure'].
  function goStep2() {
    if (!nameOk || !phoneOk || !emailOk) { setTried(true); setErr(t.e3); return }
    setErr(''); setTried(false); setStep(2)
  }
  // One Submit button (2026-10-29): nothing in step 2 is required; with no
  // tile picked the lead goes in as ['unsure'] (FUB tag Not Sure).
  function submitPicked() {
    if (!addrOk) { setTried(true); setErr(t.e2); return }
    submit(sel.length ? sel : ['unsure'])
  }

  async function submit(intents) {
    if (submitting) return
    setErr(''); setSubmitting(true)
    const { first, last } = splitName(form.name)
    try {
      const { data, error } = await supabase.rpc('submit_event_lead', {
        p_funnel_id: row.funnel_id,
        p_first_name: first,
        p_last_name: last,
        p_email: form.email.trim(),
        p_phone: form.phone.trim(),
        p_language: lang.toUpperCase(),
        p_intents: intents,
        p_property_address: intents.includes('sell') ? form.addr.trim() : null,
        p_event_ref: readEventRef(),
        p_note: form.note.trim() || null,
      })
      if (error) throw error
      const leadId = Array.isArray(data) ? data[0] : data
      setSel(intents)
      setStep(3)
      trackLead()
      // Fire-and-forget push to Follow Up Boss; a FUB failure must not change
      // what the visitor sees (same pattern as FunnelLanding).
      if (leadId) {
        supabase.functions
          .invoke('send-funnel-lead-to-fub', { body: { lead_id: leadId } })
          .then(({ data: d, error: fnErr }) => {
            if (fnErr) console.error('[event fub push] invoke failed:', fnErr)
            else if (d?.error) console.error('[event fub push] function returned error:', d.error)
          })
          .catch(e => console.error('[event fub push] threw:', e))
      }
    } catch (e) {
      console.error('[submit_event_lead] failed:', e)
      setErr(t.eSend)
    } finally {
      setSubmitting(false)
    }
  }

  const tickItems = [...t.tick, ...t.tick]

  return (
    <>
      <style>{css}</style>
      <div className="efl-root">
        <section className="efl-stage">
          <div className="efl-bg" aria-hidden="true"></div>
          <div className="efl-shade" aria-hidden="true"></div>
          <div className="efl-grid" aria-hidden="true"></div>

          <div className="efl-top-space" aria-hidden="true"></div>
          <div className={'efl-top' + (scrolled ? ' scrolled' : '')}>
            <BrokerageMark brokerage={row.brokerage} />
            <div className="efl-lang" role="group" aria-label="Language">
              <button type="button" className={lang === 'en' ? 'on' : ''} aria-pressed={lang === 'en'} onClick={() => setLang('en')}>
                <FlagUS /><span>English</span>
              </button>
              <button type="button" className={lang === 'es' ? 'on' : ''} aria-pressed={lang === 'es'} onClick={() => setLang('es')}>
                <FlagMX /><span>Español</span>
              </button>
            </div>
          </div>

          <div className="efl-wrap">
            <div>
              <span className="efl-live"><span className="efl-dot"></span>{t.live}</span>
              <h1 className="efl-h1">{t.h1a} <span className="efl-glow">{t.h1b}</span></h1>
              <p className="efl-sub">{t.subA}<b>{t.subB}</b>{t.subC}</p>
              <div className="efl-pills">
                <span className="efl-pill"><Icon.Shield />{t.p1}</span>
                <span className="efl-pill"><Icon.Lang />{t.p2}</span>
                <span className="efl-pill"><Icon.Bolt />{t.p3}</span>
              </div>
            </div>

            <div className="efl-card" ref={cardRef}>
              {step === 3 ? (
                <div className="efl-done efl-fade" key="done">
                  <div className="efl-ring">
                    <svg viewBox="0 0 84 84" fill="none" aria-hidden="true">
                      <circle cx="42" cy="42" r="38" stroke="#fdbf00" strokeWidth="4" />
                      <path d="M27 43l10 10 20-21" stroke="#fff" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <h2>{t.doneH}</h2>
                  <p>{t.doneP}</p>
                  <div className="efl-chips">{sel.map(k => <span key={k}>{chipLabel(k)}</span>)}</div>
                </div>
              ) : step === 1 ? (
                <div className="efl-fade" key="s1">
                  <div className="efl-steplbl"><span>{t.step} 1 {t.of} 2</span><span>{t.time}</span></div>
                  <div className="efl-bar"><i style={{ width: '50%' }}></i></div>
                  <div className="efl-q">{t.q2}</div>
                  <div className="efl-qs">{t.q2s}</div>
                  <Field id="efl-name" icon="user" label={t.name} value={form.name} onChange={v => set('name', v)}
                    bad={tried && !nameOk} autoComplete="name" />
                  <Field id="efl-phone" icon="phone" label={t.phone} type="tel" inputMode="tel" value={form.phone}
                    onChange={v => set('phone', v)} bad={tried && !phoneOk} autoComplete="tel" />
                  <Field id="efl-email" icon="mail" label={t.email} type="email" inputMode="email" value={form.email}
                    onChange={v => set('email', v)} bad={tried && !emailOk} autoComplete="email" />
                  {err && <div className="efl-err" role="alert">{err}</div>}
                  <button type="button" className="efl-cta" onClick={goStep2}>{t.next} <Icon.Arrow /></button>
                </div>
              ) : (
                <div className="efl-fade" key="s2">
                  <div className="efl-steplbl"><span>{t.step} 2 {t.of} 2</span><span>✓</span></div>
                  <div className="efl-bar"><i style={{ width: '100%' }}></i></div>
                  <div className="efl-q">{t.q1}</div>
                  <div className="efl-qs">{t.q1s}</div>
                  <div className="efl-tiles">
                    {INTENTS.map(k => {
                      const on = sel.includes(k)
                      const Ic = Icon[k]
                      return (
                        <button type="button" key={k} className={'efl-tile' + (on ? ' on' : '')} aria-pressed={on} onClick={() => toggle(k)}>
                          <span className="efl-ck"><Icon.Check /></span>
                          <span className="efl-ic"><Ic /></span>
                          <span className="efl-tt">{t.tiles[k][0]}</span>
                          <span className="efl-td">{t.tiles[k][1]}</span>
                        </button>
                      )
                    })}
                  </div>
                  <div className={'efl-reveal' + (selling ? ' open' : '')}>
                    <div>
                      {selling && (
                        <Field id="efl-addr" icon="pin" label={t.addr} value={form.addr} onChange={v => set('addr', v)}
                          bad={tried && !addrOk} autoComplete="street-address" />
                      )}
                    </div>
                  </div>
                  <div className={'efl-reveal' + (sel.includes('behind') ? ' open' : '')}>
                    <div><div className="efl-note"><Icon.Shield /><span>{t.noteBehind}</span></div></div>
                  </div>
                  <div className={'efl-reveal' + (sel.includes('insurance') ? ' open' : '')}>
                    <div><div className="efl-note"><Icon.Shield /><span>{t.noteIns}</span></div></div>
                  </div>
                  <div className="efl-fld efl-fld-note">
                    <textarea id="efl-note" placeholder=" " rows={3} maxLength={1000} value={form.note} onChange={e => set('note', e.target.value)} />
                    <label htmlFor="efl-note">{t.note}</label>
                  </div>
                  {err && <div className="efl-err" role="alert">{err}</div>}
                  <button type="button" className="efl-cta" onClick={submitPicked} disabled={submitting}>
                    {submitting ? t.sending : t.send} {!submitting && <Icon.Arrow />}
                  </button>
                  <button type="button" className="efl-back" onClick={() => { setStep(1); setErr(''); setTried(false) }}>{t.back}</button>
                  <p className="efl-fine">{t.fine}</p>
                </div>
              )}
            </div>
          </div>
        </section>

        <div className="efl-ticker" aria-hidden="true">
          <div>
            {tickItems.map((w, i) => (
              <React.Fragment key={i}>
                {i % 2 ? <em>{w}</em> : <span>{w}</span>}
                <span className="efl-star">✦</span>
              </React.Fragment>
            ))}
          </div>
        </div>

        <section className="efl-how">
          <h3>{t.howH}</h3>
          <div className="efl-steps">
            <div className="efl-st"><b>01</b><h4>{t.s1h}</h4><p>{t.s1p}</p></div>
            <div className="efl-st"><b>02</b><h4>{t.s2h}</h4><p>{t.s2p}</p></div>
            <div className="efl-st"><b>03</b><h4>{t.s3h}</h4><p>{t.s3p}</p></div>
          </div>
        </section>

        <footer className="efl-foot">
          <BrokerageMark brokerage={row.brokerage} footer />
          <div className="efl-legal">
            {row.brokerage}{t.legalA}{row.brokerage === 'HomePros' ? ' · ' + HOMEPROS_OFFICE : ''}{t.legalB}
          </div>
        </footer>
      </div>
    </>
  )
}

const css = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
.efl-root{--blue:#1a82ff;--gold:#fdbf00;--orange:#ff7a1a;--ink:#0a1220;--glass:rgba(10,18,32,.62);--line:rgba(255,255,255,.14);--muted:rgba(255,255,255,.72);
  background:var(--ink);color:#fff;font-family:'Plus Jakarta Sans',system-ui,-apple-system,'Segoe UI',sans-serif;-webkit-font-smoothing:antialiased;min-height:100vh;overflow-x:hidden}
.efl-root *{box-sizing:border-box}
.efl-root button,.efl-root input,.efl-root textarea{font:inherit;color:inherit}
.efl-stage{position:relative;min-height:100vh;overflow:hidden;isolation:isolate}
.efl-bg{position:absolute;inset:-4%;background:url(/event-hero.jpg) center 35%/cover no-repeat;animation:efl-drift 28s ease-in-out infinite alternate;z-index:-3}
@keyframes efl-drift{from{transform:scale(1.02)}to{transform:scale(1.12) translate(-2%,-1%)}}
.efl-shade{position:absolute;inset:0;z-index:-2;background:linear-gradient(180deg,rgba(10,18,32,.55) 0%,rgba(10,18,32,.15) 30%,rgba(10,18,32,.55) 62%,var(--ink) 92%),radial-gradient(120% 70% at 0% 100%,rgba(26,130,255,.35),transparent 60%)}
.efl-grid{position:absolute;inset:0;z-index:-1;opacity:.18;background-image:linear-gradient(rgba(255,255,255,.08) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.08) 1px,transparent 1px);background-size:44px 44px;-webkit-mask-image:linear-gradient(180deg,transparent 40%,#000 75%,transparent);mask-image:linear-gradient(180deg,transparent 40%,#000 75%,transparent)}
@media(max-width:900px){.efl-shade{background:linear-gradient(180deg,rgba(10,18,32,.78) 0%,rgba(10,18,32,.62) 35%,rgba(10,18,32,.7) 62%,var(--ink) 90%)}.efl-glow{filter:drop-shadow(0 2px 12px rgba(0,0,0,.6))}}

.efl-top{position:fixed;top:0;left:0;right:0;z-index:50;display:flex;justify-content:space-between;align-items:center;gap:12px;padding:18px clamp(16px,4vw,48px);border-bottom:1px solid transparent;transition:background .25s,padding .25s,border-color .25s}
.efl-top.scrolled{background:rgba(10,18,32,.82);-webkit-backdrop-filter:blur(16px) saturate(140%);backdrop-filter:blur(16px) saturate(140%);border-bottom-color:var(--line);padding-top:10px;padding-bottom:10px}
.efl-top.scrolled .efl-logo{height:clamp(30px,4vw,38px)}
.efl-logo{transition:height .25s}
.efl-top-space{height:calc(clamp(34px,5vw,48px) + 36px)}
.efl-logo{height:clamp(34px,5vw,48px);width:auto;display:block;filter:drop-shadow(0 2px 10px rgba(0,0,0,.45))}
.efl-brand-text{font-size:20px;font-weight:800;letter-spacing:-.01em}
.efl-lang{display:flex;background:rgba(255,255,255,.12);-webkit-backdrop-filter:blur(14px);backdrop-filter:blur(14px);border:1px solid var(--line);border-radius:999px;padding:4px;flex:none}
.efl-lang button{display:inline-flex;align-items:center;gap:7px;border:0;background:transparent;padding:7px 14px;border-radius:999px;font-size:13px;font-weight:700;cursor:pointer;color:#fff;opacity:.75;transition:.2s}
.efl-lang button.on{background:#fff;color:var(--ink);opacity:1}
.efl-flag{width:22px;height:14px;border-radius:2px;box-shadow:0 0 0 1px rgba(0,0,0,.15);flex:none}
@media(max-width:420px){.efl-lang button span{display:none}.efl-lang button{padding:7px 10px}}

.efl-wrap{display:grid;grid-template-columns:1.05fr .95fr;gap:clamp(24px,4vw,64px);align-items:center;padding:clamp(12px,4vh,56px) clamp(16px,4vw,48px) 56px;max-width:1240px;margin:0 auto}
@media(max-width:900px){.efl-wrap{grid-template-columns:minmax(0,1fr);padding-top:8px}}
.efl-live{display:inline-flex;align-items:center;gap:10px;background:rgba(10,18,32,.55);-webkit-backdrop-filter:blur(10px);backdrop-filter:blur(10px);border:1px solid var(--line);padding:7px 14px 7px 10px;border-radius:999px;font-size:12px;font-weight:700;letter-spacing:.12em;text-transform:uppercase}
.efl-dot{width:9px;height:9px;border-radius:50%;background:#ff3b3b;animation:efl-pulse 1.6s infinite}
@keyframes efl-pulse{0%{box-shadow:0 0 0 0 rgba(255,59,59,.7)}70%{box-shadow:0 0 0 10px rgba(255,59,59,0)}100%{box-shadow:0 0 0 0 rgba(255,59,59,0)}}
.efl-h1{font-size:clamp(40px,6.4vw,78px);line-height:.98;font-weight:800;letter-spacing:-.035em;margin:18px 0 16px;text-shadow:0 4px 30px rgba(0,0,0,.35)}
.efl-glow{background:linear-gradient(90deg,var(--gold),var(--orange),var(--gold));background-size:200% auto;-webkit-background-clip:text;background-clip:text;color:transparent;animation:efl-sheen 5s linear infinite}
@keyframes efl-sheen{to{background-position:200% center}}
.efl-sub{font-size:clamp(16px,1.6vw,19px);line-height:1.55;color:var(--muted);max-width:520px;margin:0}
.efl-sub b{color:#fff;font-weight:700}
.efl-pills{display:flex;flex-wrap:wrap;gap:10px;margin-top:24px}
.efl-pill{display:flex;align-items:center;gap:8px;font-size:13px;font-weight:600;padding:9px 14px;border-radius:12px;background:rgba(255,255,255,.08);border:1px solid var(--line)}
.efl-pill svg{width:16px;height:16px;color:var(--gold)}
@media(max-width:900px){.efl-pills{display:none}}

.efl-card{position:relative;background:var(--glass);-webkit-backdrop-filter:blur(22px) saturate(140%);backdrop-filter:blur(22px) saturate(140%);border-radius:24px;padding:26px;box-shadow:0 30px 80px rgba(0,0,0,.45);scroll-margin-top:84px}
.efl-card::before{content:"";position:absolute;inset:0;border-radius:24px;padding:1px;background:linear-gradient(135deg,rgba(26,130,255,.9),rgba(255,255,255,.12) 40%,rgba(253,191,0,.9));-webkit-mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);-webkit-mask-composite:xor;mask-composite:exclude;pointer-events:none}
.efl-bar{height:5px;border-radius:5px;background:rgba(255,255,255,.12);overflow:hidden;margin-bottom:20px}
.efl-bar i{display:block;height:100%;border-radius:5px;background:linear-gradient(90deg,var(--blue),var(--gold));transition:width .5s cubic-bezier(.2,.8,.2,1)}
.efl-steplbl{display:flex;justify-content:space-between;font-size:12px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:var(--muted);margin-bottom:8px}
.efl-steplbl span:last-child{color:var(--gold)}
.efl-q{font-size:22px;font-weight:800;letter-spacing:-.02em;margin-bottom:4px}
.efl-qs{font-size:14px;color:var(--muted);margin-bottom:16px}

.efl-tiles{display:grid;grid-template-columns:1fr 1fr;gap:10px}
.efl-tile{position:relative;display:flex;flex-direction:column;align-items:flex-start;text-align:left;padding:16px 14px 14px;border-radius:16px;border:1px solid var(--line);background:rgba(255,255,255,.06);cursor:pointer;transition:transform .15s,border-color .2s,background .2s,box-shadow .2s}
.efl-tile:hover{transform:translateY(-2px);border-color:rgba(255,255,255,.35)}
.efl-ic{width:40px;height:40px;border-radius:12px;display:grid;place-items:center;background:rgba(255,255,255,.1);margin-bottom:12px;transition:.2s}
.efl-ic svg{width:22px;height:22px}
.efl-tt{font-weight:800;font-size:16px;line-height:1.2;padding-right:22px}
.efl-td{font-size:12px;color:var(--muted);margin-top:3px}
.efl-ck{position:absolute;top:12px;right:12px;width:22px;height:22px;border-radius:50%;border:1.5px solid rgba(255,255,255,.35);display:grid;place-items:center;transition:.2s}
.efl-ck svg{width:12px;height:12px;opacity:0}
.efl-tile.on{border-color:var(--blue);background:rgba(26,130,255,.18);box-shadow:0 0 0 1px var(--blue),0 10px 30px rgba(26,130,255,.35)}
.efl-tile.on .efl-ic{background:var(--blue)}
.efl-tile.on .efl-ck{background:var(--gold);border-color:var(--gold);color:var(--ink)}
.efl-tile.on .efl-ck svg{opacity:1}

.efl-reveal{display:grid;grid-template-rows:0fr;transition:grid-template-rows .4s cubic-bezier(.2,.8,.2,1)}
.efl-reveal.open{grid-template-rows:1fr}
.efl-reveal>div{overflow:hidden}
.efl-note{display:flex;gap:10px;align-items:flex-start;font-size:13px;line-height:1.45;padding:12px 14px;border-radius:12px;background:rgba(253,191,0,.12);border:1px solid rgba(253,191,0,.4);margin-top:12px}
.efl-note svg{flex:0 0 18px;width:18px;height:18px;color:var(--gold);margin-top:1px}

.efl-fld{position:relative;margin-top:12px}
.efl-fld input{width:100%;height:56px;padding:22px 16px 8px 46px;border-radius:14px;border:1px solid var(--line);background:rgba(255,255,255,.07);outline:none;transition:.2s;font-size:16px}
.efl-fld input:focus{border-color:var(--blue);background:rgba(26,130,255,.1);box-shadow:0 0 0 4px rgba(26,130,255,.18)}
.efl-fld label{position:absolute;left:46px;top:18px;font-size:15px;color:var(--muted);pointer-events:none;transition:.18s}
.efl-fld-note textarea{width:100%;min-height:96px;padding:26px 16px 10px;border-radius:14px;border:1px solid var(--line);background:rgba(255,255,255,.07);outline:none;transition:.2s;font-size:16px;resize:vertical;line-height:1.45}
.efl-fld-note textarea:focus{border-color:var(--blue);background:rgba(26,130,255,.1);box-shadow:0 0 0 4px rgba(26,130,255,.18)}
.efl-fld-note label{left:16px}
.efl-fld-note textarea:focus+label,.efl-fld-note textarea:not(:placeholder-shown)+label{top:8px;font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--gold)}
.efl-fld input:focus+label,.efl-fld input:not(:placeholder-shown)+label{top:8px;font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--gold)}
.efl-fld svg{position:absolute;left:15px;top:18px;width:20px;height:20px;color:var(--muted)}
.efl-fld.bad input{border-color:#ff5a5a;background:rgba(255,90,90,.08)}
.efl-err{color:#ff8a8a;font-size:13px;font-weight:600;margin-top:12px}

.efl-cta{position:relative;overflow:hidden;width:100%;margin-top:18px;border:0;border-radius:14px;padding:17px;font-size:17px;font-weight:800;letter-spacing:-.01em;color:#fff;background:linear-gradient(90deg,var(--blue),#3d8bff);cursor:pointer;box-shadow:0 12px 30px rgba(26,130,255,.45);display:flex;justify-content:center;align-items:center;gap:10px;transition:transform .1s}
.efl-cta:active:not(:disabled){transform:translateY(1px)}
.efl-cta:disabled{opacity:.7;cursor:wait}
.efl-cta::after{content:"";position:absolute;top:0;left:-60%;width:40%;height:100%;background:linear-gradient(90deg,transparent,rgba(255,255,255,.45),transparent);transform:skewX(-20deg);animation:efl-shine 3.2s ease-in-out infinite}
@keyframes efl-shine{0%,60%{left:-60%}100%{left:130%}}
.efl-cta svg{width:20px;height:20px}
.efl-back{background:none;border:0;color:var(--muted);font-size:13px;font-weight:600;cursor:pointer;margin:12px auto 0;display:block}
.efl-fine{font-size:11px;line-height:1.5;color:rgba(255,255,255,.55);text-align:center;margin:12px 0 0}

.efl-done{text-align:center;padding:18px 4px 8px}
.efl-ring{width:84px;height:84px;margin:0 auto 18px;border-radius:50%;display:grid;place-items:center;background:radial-gradient(circle,rgba(253,191,0,.25),transparent 70%)}
.efl-ring svg{width:84px;height:84px}
.efl-ring circle{stroke-dasharray:240;stroke-dashoffset:240;animation:efl-draw .8s ease forwards}
.efl-ring path{stroke-dasharray:60;stroke-dashoffset:60;animation:efl-draw .5s .6s ease forwards}
@keyframes efl-draw{to{stroke-dashoffset:0}}
.efl-done h2{font-size:30px;font-weight:800;letter-spacing:-.02em;margin:0 0 8px}
.efl-done p{color:var(--muted);line-height:1.55;margin:0}
.efl-chips{display:flex;flex-wrap:wrap;justify-content:center;gap:8px;margin-top:16px}
.efl-chips span{font-size:12px;font-weight:700;padding:6px 12px;border-radius:999px;background:rgba(26,130,255,.2);border:1px solid rgba(26,130,255,.5)}
.efl-fade{animation:efl-fade .45s cubic-bezier(.2,.8,.2,1)}
@keyframes efl-fade{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}

.efl-ticker{border-top:1px solid var(--line);border-bottom:1px solid var(--line);background:#0d1828;overflow:hidden;white-space:nowrap}
.efl-ticker>div{display:inline-flex;gap:38px;padding:16px 0;animation:efl-tick 30s linear infinite;font-weight:800;font-size:15px;letter-spacing:.14em;text-transform:uppercase}
.efl-ticker em{font-style:normal;color:var(--gold)}
.efl-star{opacity:.35}
@keyframes efl-tick{to{transform:translateX(-50%)}}

.efl-how{max-width:1240px;margin:0 auto;padding:56px clamp(16px,4vw,48px)}
.efl-how h3{font-size:clamp(26px,3.4vw,38px);font-weight:800;letter-spacing:-.03em;margin:0 0 24px}
.efl-steps{display:grid;grid-template-columns:repeat(3,1fr);gap:14px}
@media(max-width:760px){.efl-steps{grid-template-columns:1fr}}
.efl-st{padding:22px;border-radius:20px;background:linear-gradient(180deg,rgba(255,255,255,.06),rgba(255,255,255,.02));border:1px solid var(--line)}
.efl-st b{display:block;font-size:13px;letter-spacing:.14em;color:var(--gold);margin-bottom:12px}
.efl-st h4{font-size:19px;font-weight:800;margin:0 0 6px}
.efl-st p{font-size:14px;line-height:1.55;color:var(--muted);margin:0}

.efl-foot{max-width:1240px;margin:0 auto;padding:22px clamp(16px,4vw,48px) 40px;border-top:1px solid var(--line);font-size:12px;line-height:1.6;color:rgba(255,255,255,.55);display:flex;gap:16px;justify-content:space-between;align-items:flex-start;flex-wrap:wrap}
.efl-logo-foot{height:24px;filter:none;opacity:.9}
.efl-brand-foot{font-size:15px}
.efl-legal{max-width:760px}

@media(prefers-reduced-motion:reduce){.efl-bg,.efl-glow,.efl-cta::after,.efl-ticker>div,.efl-dot{animation:none}}
`
